export type PinnableColumn = {
  pinned?: boolean;
  unpinnedIndex?: number;
};

export const orderPinnedColumns = <T extends PinnableColumn>(
  columns: readonly T[]
): T[] => [
  ...columns.filter((column) => column.pinned),
  ...columns.filter((column) => !column.pinned),
];

export const setColumnPinned = <T extends PinnableColumn>(
  columns: readonly T[],
  index: number,
  pinned: boolean
): T[] => {
  const column = columns[index];
  if (!column || !!column.pinned === pinned) {
    return [...columns];
  }

  // Rebuild the order before pinning. Saved slots include other pinned
  // columns, so unpinning several columns in any order restores them all.
  const rememberedColumns = columns
    .filter((entry) => entry.pinned && entry.unpinnedIndex !== undefined)
    .sort((a, b) => a.unpinnedIndex! - b.unpinnedIndex!);
  const originalOrder = columns.filter(
    (entry) => !rememberedColumns.includes(entry)
  );
  rememberedColumns.forEach((entry) => {
    originalOrder.splice(entry.unpinnedIndex!, 0, entry);
  });

  const updatedColumn = {
    ...column,
    pinned,
    unpinnedIndex: pinned ? originalOrder.indexOf(column) : undefined,
  };
  if (!pinned && column.unpinnedIndex !== undefined) {
    return [
      ...columns.filter((entry) => entry !== column && entry.pinned),
      ...originalOrder
        .map((entry) => (entry === column ? updatedColumn : entry))
        .filter((entry) => !entry.pinned),
    ];
  }

  // Older preferences have no saved slot; retain their existing fallback.
  const remainingColumns = columns.filter(
    (_column, columnIndex) => columnIndex !== index
  );
  const normalizedColumns = orderPinnedColumns(remainingColumns);
  const pinnedCount = normalizedColumns.filter(
    (column) => column.pinned
  ).length;

  normalizedColumns.splice(pinnedCount, 0, updatedColumn);
  return normalizedColumns;
};
